import React, { useEffect, useState } from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import LogoutIcon from '@mui/icons-material/Logout';
import { styled } from '@mui/material/styles';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import {
  Toolbar,
  IconButton,
  Typography,
  Badge,
  LinearProgress,
  Box,
  Menu,
  MenuItem,
  Button,
} from '@mui/material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ArtistSelector from '../Common/ArtistSelector';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  dashboardState,
  loadingState,
  userState,
} from '../../state/RecoilState';
import { useCookies } from 'react-cookie';
import { drawerWidth } from '../../Constants';

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  borderBottom: '1px solid rgb(37, 45, 85)',
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

interface Props {
  open: boolean;
  toggleDrawer: () => void;
}

const TopAppBar: React.FC<Props> = ({ open, toggleDrawer }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [user, setUser] = useRecoilState<any>(userState);
  const dashboard = useRecoilValue(dashboardState);
  const loading = useRecoilValue(loadingState);
  const [, , removeCookie] = useCookies();
  const [showArtistSelect, setShowArtistSelect] = useState(false);
  const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(
    null
  );
  const registrants = dashboard ? `${dashboard.registrantCount}` : '0';

  const onSignoutClick = () => {
    removeCookie('token');
    setUser(undefined);
    navigate('/login');
  };

  useEffect(() => {
    const page = pages.find((p) => p.path === pathname);
    if (!page) return;
    setShowArtistSelect(!!page.showArtistSelect);
  }, [pathname]);

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  return (
    <AppBar position='absolute' open={open} className='top-app-bar'>
      <Toolbar sx={{ pr: '24px', display: 'flex', alignItems: 'center' }}>
        {user.role === 'Administrator' && (
          <IconButton
            edge='start'
            color='inherit'
            aria-label='open drawer'
            onClick={toggleDrawer}
            sx={{
              marginRight: '36px',
              ...(open && { display: 'none' }),
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        )}
        <Typography component='h1' variant='h6' color='inherit' noWrap>
          MFS Admin
        </Typography>
        <Box sx={{ flexGrow: 1, display: { xs: 'none', lg: 'flex' } }}>
          <Typography
            sx={{
              flexGrow: 1,
              mt: '4px',
              ml: 3,
            }}
            justifyContent={'flex-start'}
            variant='button'
          >
            {pages.map((page) => {
              if (
                (page.roles && page.roles.includes(user.role)) ||
                !page.roles
              ) {
                return (
                  <Link
                    key={page.title}
                    to={page.path}
                    style={{
                      color: 'inherit',
                      textDecoration: 'none',
                      margin: '0 10px',
                    }}
                  >
                    {page.title}
                  </Link>
                );
              } else {
                return null;
              }
            })}
          </Typography>
        </Box>
        <Box sx={{ flexGrow: 1, display: { xs: 'flex', lg: 'none' } }}>
          <Button
            size='small'
            onClick={handleOpenNavMenu}
            color='inherit'
            sx={{ p: 0, mt: '4px' }}
          >
            Menu
          </Button>
          <Menu
            id='menu-appbar'
            anchorEl={anchorElNav}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            open={Boolean(anchorElNav)}
            onClose={handleCloseNavMenu}
            sx={{
              display: { xs: 'block', lg: 'none' },
            }}
          >
            {pages.map((page) => (
              <MenuItem key={page.title} onClick={handleCloseNavMenu}>
                <Typography textAlign='center'>
                  <Link
                    key={page.title}
                    to={page.path}
                    style={{
                      color: 'inherit',
                      textDecoration: 'none',
                      margin: '0 10px',
                    }}
                  >
                    {page.title}
                  </Link>
                </Typography>
              </MenuItem>
            ))}
          </Menu>
        </Box>

        {!!user && (
          <>
            <Box display={'flex'} alignItems={'center'}>
              {showArtistSelect &&
                (user.role === 'Administrator' ||
                  user.role === 'ContentAdmin') && <ArtistSelector />}
            </Box>
            <IconButton
              edge='start'
              color='inherit'
              aria-label='logout'
              onClick={onSignoutClick}
              sx={{ ml: 1 }}
            >
              <LogoutIcon />
            </IconButton>
          </>
        )}

        {user.role === 'Adminsistrator' &&
          dashboard?.registrantCount &&
          dashboard?.registrantCount > 0 && (
            <IconButton color='inherit' sx={{ fontSize: 10 }}>
              {dashboard?.nextShowcase ? dashboard.nextShowcase.city : ''}{' '}
              Showcase Registrants{' '}
              <Badge badgeContent={registrants} color='secondary'>
                <HowToRegIcon />
              </Badge>
            </IconButton>
          )}
      </Toolbar>
      {loading && <LinearProgress color='primary' />}
    </AppBar>
  );
};

export default TopAppBar;

const pages = [
  { title: 'Songs', path: '/songs', showArtistSelect: true },
  {
    title: 'Images',
    path: '/images',
    showArtistSelect: true,
    roles: ['Administrator', 'ContentAdmin'],
  },
  {
    title: 'Videos',
    path: '/videos',
    showArtistSelect: true,
    roles: ['Administrator', 'ContentAdmin'],
  },
  { title: 'YouTube', path: '/youtube', showArtistSelect: true },
  { title: 'Audio', path: '/audio', showArtistSelect: true },
  { title: 'Files', path: '/files', showArtistSelect: true },
  {
    title: 'Satellite Content',
    path: '/satellite',
    showArtistSelect: true,
    roles: ['Administrator', 'ContentAdmin'],
  },
  {
    title: 'Artist Biog',
    path: '/biog',
    showArtistSelect: true,
    roles: ['Administrator', 'ContentAdmin'],
  },
  {
    title: 'MFS Content',
    path: '/content',
    roles: ['Administrator', 'ContentAdmin'],
  },
  {
    title: 'Blog',
    path: '/bloglist',
    roles: ['Administrator', 'ContentAdmin'],
  },
];
